import React, { useState } from "react";
import { Download } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { toCsv, saveCsv, TRANSACTION_COLUMNS } from "../../utils/csv";
import { PERIODS, isInPeriod, periodLabel } from "../../utils/period";
import { sortByOption } from "../../utils/sort";
import { paidFromName } from "../../utils/bills";
import { missingPaidFrom, missingReceivedInto, paidFromFields, receivedIntoId } from "../../utils/moneySources";
import MoneySourceSelect from "../../components/MoneySourceSelect";
import SettleSheet from "./SettleSheet";
import PaidAmountSheet from "./PaidAmountSheet";
import { Card, Chip, ChipRow, StatusPill, Sheet, Button, Empty, LinkButton, useInputStyle, useMoney, formatDateLong } from "../ui";

// `row` is kept for the settle sheet and the account checks.
const toEntry = (row, kind) =>
  kind === "income"
    ? { key: `i${row.id}`, id: row.id, kind, date: row.date, label: row.source, type: row.type, amount: row.amount, status: row.status, description: row.description, repaid: !!row.repaidBillId, row }
    : { key: `b${row.id}`, id: row.id, kind, date: row.dueDate, label: row.payee, type: row.type, amount: row.amount, status: row.status, description: row.description, autopay: !!row.autopay, repaid: !!row.repaidIncomeId, paidFromKind: row.paidFromKind, paidFromId: row.paidFromId, row };

// Dated after the first account or card was added, but doesn't say which one it went through.
const needsSource = (e, data) => (e.kind === "income" ? missingReceivedInto(e.row, data.assets, data.cards) : missingPaidFrom(e.row, data.assets, data.cards));

const accountName = (id, data) => {
  const a = (data.assets || []).find((x) => x.id === id);
  return a ? a.name : "a deleted account";
};

const DetailSheet = ({ entry, onClose, data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const input = useInputStyle();
  const isIncome = entry.kind === "income";
  const collection = isIncome ? "income" : "bills";
  const doneStatus = isIncome ? "Received" : "Paid";
  const undoneStatus = isIncome ? "Upcoming" : "Pending";
  const isDone = entry.status === doneStatus;
  const [settling, setSettling] = useState(null); // "lent" | "borrowed"
  const [source, setSource] = useState("");
  const [askAmount, setAskAmount] = useState(false);
  const missing = needsSource(entry, data);
  // Lent money that came back / borrowed money paid back is settled with a sheet that
  // asks which account it went through.
  const settleKind = entry.type === "Lent" && !entry.repaid ? "lent" : entry.type === "Borrowed" && isDone && !entry.repaid ? "borrowed" : null;

  if (settling) return <SettleSheet kind={settling} entry={entry.row} data={data} onClose={onClose} />;
  if (askAmount) return <PaidAmountSheet bill={entry.row} data={data} onClose={onClose} />;
  // A bill whose amount changes each time asks for what was actually paid.
  const toggleDone = () => {
    if (!isIncome && !isDone && entry.row.variableAmount) return setAskAmount(true);
    data.update(collection, entry.id, { status: isDone ? undoneStatus : doneStatus });
    onClose();
  };

  const saveSource = () => {
    if (!source) return;
    data.update(collection, entry.id, isIncome ? { receivedIntoId: receivedIntoId(source) } : paidFromFields(source));
    onClose();
  };

  return (
    <Sheet title={entry.label} onClose={onClose}>
      <div style={{ fontFamily: fonts.mono, fontSize: 28, fontWeight: 700, color: isIncome ? tokens.positive : tokens.textPrimary }}>
        {isIncome ? "+" : "-"}{money(entry.amount)}
      </div>
      <div style={{ margin: "8px 0 16px", fontSize: 13, color: tokens.textMuted }}>
        {entry.type} · {formatDateLong(entry.date)}{entry.autopay ? " · Autopay" : ""}
        {entry.paidFromKind ? ` · from ${paidFromName(entry, data)}` : ""}
        {isIncome && entry.row.receivedIntoId ? ` · into ${accountName(entry.row.receivedIntoId, data)}` : ""}
      </div>
      {!!entry.description && <p style={{ margin: "0 0 18px", fontSize: 14, color: tokens.textPrimary }}>{entry.description}</p>}
      {entry.type === "Lent" && entry.repaid && (
        <p style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: tokens.positive }}>Paid back. It's in your money received.</p>
      )}
      {entry.type === "Borrowed" && entry.repaid && (
        <p style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: tokens.positive }}>You've paid this back.</p>
      )}
      {missing && (
        <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, backgroundColor: tokens.pendingBg }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: tokens.pending, marginBottom: 8 }}>
            {isIncome ? "Which account did it go into?" : "Where was it paid from?"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <MoneySourceSelect style={input} value={source} onChange={setSource} assets={data.assets} cards={data.cards} accountsOnly={isIncome} required />
            </div>
            <Button disabled={!source} onClick={saveSource}>Save</Button>
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {settleKind && <Button onClick={() => setSettling(settleKind)}>{settleKind === "lent" ? "Got it back" : "Paid it back"}</Button>}
        <Button kind={settleKind ? "ghost" : "primary"} onClick={toggleDone}>
          {isDone ? `Mark as ${undoneStatus.toLowerCase()}` : `Mark as ${doneStatus.toLowerCase()}`}
        </Button>
        <Button kind="danger" onClick={() => { data.remove(collection, entry.id); onClose(); }}>Delete</Button>
      </div>
    </Sheet>
  );
};

const ActivityScreen = ({ data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [period, setPeriod] = useState("Month");
  const [kindFilter, setKindFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const entries = sortByOption(
    [...data.bills.map((b) => toEntry(b, "expense")), ...data.income.map((i) => toEntry(i, "income"))]
      .filter((e) => isInPeriod(e.date, period))
      .filter((e) => kindFilter === "all" || e.kind === kindFilter),
    "date-desc"
  );

  const totalIn = entries.filter((e) => e.kind === "income").reduce((s, e) => s + e.amount, 0);
  const totalOut = entries.filter((e) => e.kind === "expense").reduce((s, e) => s + e.amount, 0);
  const net = totalIn - totalOut;

  // Group by day for the timeline
  const days = [];
  for (const e of entries) {
    const last = days[days.length - 1];
    if (last && last.date === e.date) last.items.push(e);
    else days.push({ date: e.date, items: [e] });
  }

  const current = selected && entries.find((e) => e.key === selected);

  return (
    <div>
      {/* What to show on the left, the period (This Month by default) on the right. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ChipRow>
            <Chip active={kindFilter === "all"} onClick={() => setKindFilter("all")}>All</Chip>
            <Chip active={kindFilter === "income"} onClick={() => setKindFilter("income")}>Income</Chip>
            <Chip active={kindFilter === "expense"} onClick={() => setKindFilter("expense")}>Expenses</Chip>
          </ChipRow>
        </div>
        <select
          aria-label="Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          style={{ flexShrink: 0, marginBottom: 6, padding: "8px 10px", borderRadius: 999, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.surface, color: tokens.textPrimary, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}
        >
          {PERIODS.map((p) => <option key={p} value={p}>{periodLabel(p)}</option>)}
        </select>
      </div>

      <Card style={{ marginTop: 10, display: "flex", justifyContent: "space-between", padding: "14px 16px" }}>
        {[["In", totalIn, tokens.positive], ["Out", totalOut, tokens.negative], ["Net", Math.abs(net), net >= 0 ? tokens.positive : tokens.negative]].map(([label, value, color]) => (
          <div key={label}>
            <div style={{ fontSize: 12, color: tokens.textMuted }}>{label}</div>
            <div style={{ marginTop: 3, fontFamily: fonts.mono, fontSize: 15, fontWeight: 700, color }}>
              {label === "Net" ? (net >= 0 ? "+" : "-") : ""}{money(value)}
            </div>
          </div>
        ))}
      </Card>
      {entries.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <LinkButton icon={Download} onClick={() => saveCsv(`budgetsprint-${periodLabel(period).toLowerCase().replace(/\s+/g, "-")}.csv`, toCsv(entries, TRANSACTION_COLUMNS))}>
            Export as spreadsheet
          </LinkButton>
        </div>
      )}

      {days.length === 0 && <Empty>Nothing in this period.</Empty>}

      {days.map((day) => (
        <div key={day.date}>
          <div style={{ margin: "18px 4px 8px", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", color: tokens.textMuted }}>
            {formatDateLong(day.date).toUpperCase()}
          </div>
          <Card style={{ padding: "2px 16px" }}>
            {day.items.map((e, i) => (
              <button
                key={e.key}
                onClick={() => setSelected(e.key)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 0", background: "none", border: "none", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none", textAlign: "left", cursor: "pointer", color: tokens.textPrimary }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.label}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.type}{e.description ? ` · ${e.description}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: e.kind === "income" ? tokens.positive : tokens.textPrimary }}>
                    {e.kind === "income" ? "+" : "-"}{money(e.amount)}
                  </div>
                  <div style={{ marginTop: 4 }}><StatusPill label={e.status} /></div>
                  {needsSource(e, data) && (
                    <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: tokens.pending }}>{e.kind === "income" ? "Set account" : "Set Paid from"}</div>
                  )}
                </div>
              </button>
            ))}
          </Card>
        </div>
      ))}

      {current && <DetailSheet entry={current} data={data} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default ActivityScreen;
